import React, { FC } from 'react';
import { mergeCrossSellWithUpsell } from '@utils/helpers';
import Cookie from 'js-cookie';
import { useTranslations } from '@lang';
import {
  SpaEntityContainer,
  SpaEntityContainerProps,
  parseSpaEntityData,
} from '@common/components/SpaEntity/SpaEntityContainer';
import { FacilityConfigWizardContainer } from './FacilityConfigWizard/FacilityConfigWizardContainer';
import { SpaEntityError } from '@common/components/SpaEntity/SpaEntityError';
import { PageContext, RoutingData } from '@common/components/PageContext';
import { Routes } from '@common/constants';
import { useLabels } from '@common/features/Menu/useLabels';
import { useGetSpaSettings } from '@common/features/spa';
import { useGetSpaFacility } from '@common/features/spa/hooks';
import { Spinner } from '@common/components';

export const Facility: FC<SpaEntityContainerProps> = ({
  bookingStep,
  property,
  language,
  basketGuests,
  basketDetails,
  updateBasket,
  createBasket,
  signIn,
  destroy,
  upSellName,
  defaultTitle,
  entityType,
  userHasAccessToFacilities = false,
  id,
}) => {
  const { t } = useTranslations();
  const labels = useLabels();
  const { data: spaEntity, isFetching } = useGetSpaFacility(property.id, language, id, null);
  const { data: spaSettings } = useGetSpaSettings(property.id);

  const spaTermsAndConditions = spaSettings?.termsAndConditions || '';

  const parentPageName = t('spa.facility');
  const routingData: RoutingData = {
    message: t('common.backToModuleRoot', {
      entity: labels?.facilities || t('app.facilities'),
    }),
    href: Routes.FACILITY_LIST,
  };

  if (!spaEntity || isFetching) {
    return <Spinner showDelayMessage isActive={isFetching} />;
  }

  const mergedItems = mergeCrossSellWithUpsell(
    spaEntity.upSell,
    upSellName,
    spaEntity.crossSellObjects
  );

  const handleBasketUpdate = (data) => {
    const parsedEntity = parseSpaEntityData(data, spaEntity, entityType);

    const basketId = Cookie.get('basketId');

    if (basketId) {
      return updateBasket(
        property.id,
        basketId,
        language,
        {
          items: [...basketDetails.items, parsedEntity],
        },
        property.slug,
        property.timezone
      );
    }
    return createBasket(
      property.id,
      language,
      {
        items: [parsedEntity],
      },
      property.slug,
      property.timezone
    );
  };

  const facilitiesVisibleToMembers =
    property?.modulesExcludingDisabled?.spa?.features?.facilities?.features?.visibleToMembers
      ?.enabled;

  return (
    <PageContext value={{ parentPageName, routingData }}>
      {userHasAccessToFacilities ? (
        <SpaEntityContainer
          spaEntity={spaEntity}
          spaTermsAndConditions={spaTermsAndConditions}
          bookingStep={bookingStep}
          property={property}
          language={language}
          basketGuests={basketGuests}
          basketDetails={basketDetails}
          signIn={signIn}
          destroy={destroy}
          defaultTitle={defaultTitle}
          SpaEntityConfigurationWizard={FacilityConfigWizardContainer}
          mergedItems={mergedItems}
          handleBasketUpdate={handleBasketUpdate}
        />
      ) : (
        <SpaEntityError
          errorMessage={facilitiesVisibleToMembers ? 'app.mustBeMember' : 'app.mustBeLoggedIn'}
          values={{
            spaEntity: 'facilities',
          }}
        />
      )}
    </PageContext>
  );
};
